import { getPlayerCombatAbilitiesUnlockedBetweenLevels } from "./player-combat-unlocks-between-levels";
import {
	ABILITY_POINTS_PER_LEVEL_UP,
	applyExpGain,
	type CharacterStatsFromTotalExp,
	STAT_POINTS_PER_LEVEL_UP,
} from "./player-exp-level";

export type CompletionOverlaySource = "topic" | "battle" | "scenario";

export interface CompletionOverlayPlanInput {
	source: CompletionOverlaySource;
	characterStats: CharacterStatsFromTotalExp;
	/** Stop `completionExp` (non-negative). */
	expReward: number;
	assignedStatPoints?: number;
	assignedAbilityPoints?: number;
	stopRewardSummary?: {
		expGrantedTotal: number;
		levelsGainedTotal: number;
	};
	/**
	 * Levels gained from `Player.totalExp` crossing boundaries (before vs after a grant).
	 * Required when `levelsGainedTotal` on the stop rollup is not incremented for EXP-driven
	 * level-ups (typical `grantStopRewardEvent` rows use `levelsGained: 0` on the event).
	 */
	levelsGainedFromExp?: number;
	battleOutcome?: "victory" | "defeat";
	scenarioOutcome?: "success" | "failure";
	/**
	 * When set, a scenario debrief slide is appended (same id pattern as learn UI).
	 * Omit when the server has no debrief payload.
	 */
	scenarioDebriefOutcome?: "success" | "failure";
}

export type CompletionNotificationStep =
	| { type: "stop_completed" }
	| {
			type: "level_up_quote";
			newLevel: number;
			levelsGainedDelta: number;
			milestoneLevel: boolean;
	  }
	| { type: "level_up_moves" }
	| { type: "stat_points"; points: number }
	| { type: "ability_points"; points: number };

export interface CompletionOverlayPlan {
	/** Ordered `CompletionSlide.id` values (matches learn `buildCompletionSlides`). */
	slideIds: string[];
	/** In chronological slide order; caller maps to DB payloads and inserts oldest → newest if desired. */
	notificationSteps: CompletionNotificationStep[];
}

function computeUnspentPools(params: {
	newLevel: number;
	assignedStatPoints: number;
	assignedAbilityPoints: number;
}): { statPointsForSlide: number; abilityPointsForSlide: number } {
	const lifetimeStatEarned = STAT_POINTS_PER_LEVEL_UP * params.newLevel;
	const lifetimeAbilityEarned = ABILITY_POINTS_PER_LEVEL_UP * params.newLevel;
	return {
		statPointsForSlide: Math.max(
			0,
			lifetimeStatEarned - params.assignedStatPoints,
		),
		abilityPointsForSlide: Math.max(
			0,
			lifetimeAbilityEarned - params.assignedAbilityPoints,
		),
	};
}

/**
 * Single source of truth for which completion slides exist and which inbox notifications
 * align with those slices. Mirrors learn `buildCompletionSlides` ordering.
 */
export function computeCompletionOverlayPlan(
	input: CompletionOverlayPlanInput,
): CompletionOverlayPlan {
	const expReward = Math.max(0, Math.floor(input.expReward));
	const _totalExpEarned =
		input.stopRewardSummary &&
		typeof input.stopRewardSummary.expGrantedTotal === "number"
			? Math.max(0, Math.floor(input.stopRewardSummary.expGrantedTotal))
			: expReward;
	const levelsGainedTotal =
		input.stopRewardSummary &&
		typeof input.stopRewardSummary.levelsGainedTotal === "number"
			? Math.max(0, Math.floor(input.stopRewardSummary.levelsGainedTotal))
			: 0;
	const expForPreview =
		input.source === "battle" && input.battleOutcome === "defeat"
			? 0
			: input.source === "scenario" && input.scenarioOutcome === "failure"
				? 0
				: 0;
	const preview = applyExpGain(input.characterStats, expForPreview);
	const newLevel = preview.next.currentLevel;
	const fromRollupOrPreview =
		levelsGainedTotal > 0 ? levelsGainedTotal : preview.levelsGained;
	const fromExpBoundaries =
		typeof input.levelsGainedFromExp === "number"
			? Math.max(0, Math.floor(input.levelsGainedFromExp))
			: 0;
	const levelsGained = Math.max(fromRollupOrPreview, fromExpBoundaries);

	const slideIds: string[] = [];
	const notificationSteps: CompletionNotificationStep[] = [];

	if (input.source === "topic") {
		slideIds.push("completion-topic-success");
	} else if (input.source === "battle") {
		const outcome = input.battleOutcome ?? "victory";
		slideIds.push(
			outcome === "defeat"
				? "completion-battle-defeat"
				: "completion-battle-victory",
		);
		slideIds.push("completion-battle-quiz-analysis");
	} else {
		const scenarioOutcome = input.scenarioOutcome ?? "success";
		slideIds.push(
			scenarioOutcome === "failure"
				? "completion-scenario-failed"
				: "completion-scenario-success",
		);
		if (scenarioOutcome === "success" || scenarioOutcome === "failure") {
			slideIds.push("completion-scenario-analysis");
		}
		if (input.scenarioDebriefOutcome !== undefined) {
			slideIds.push(
				`completion-scenario-debrief-${input.scenarioDebriefOutcome}`,
			);
		}
	}

	if (levelsGained > 0) {
		const oldLevel = Math.max(1, newLevel - levelsGained);
		const moves = getPlayerCombatAbilitiesUnlockedBetweenLevels(
			oldLevel,
			newLevel,
		);

		const hasHudAssignmentTotals =
			typeof input.assignedStatPoints === "number" &&
			Number.isFinite(input.assignedStatPoints) &&
			typeof input.assignedAbilityPoints === "number" &&
			Number.isFinite(input.assignedAbilityPoints);
		const assignedSp = hasHudAssignmentTotals
			? Math.max(0, Math.floor(input.assignedStatPoints as number))
			: null;
		const assignedAp = hasHudAssignmentTotals
			? Math.max(0, Math.floor(input.assignedAbilityPoints as number))
			: null;

		const totalStatPoints = levelsGained * STAT_POINTS_PER_LEVEL_UP;
		const totalAbilityPoints = levelsGained * ABILITY_POINTS_PER_LEVEL_UP;

		const pools =
			hasHudAssignmentTotals && assignedSp !== null && assignedAp !== null
				? computeUnspentPools({
						newLevel,
						assignedStatPoints: assignedSp,
						assignedAbilityPoints: assignedAp,
					})
				: {
						statPointsForSlide: totalStatPoints,
						abilityPointsForSlide: totalAbilityPoints,
					};

		slideIds.push("completion-level-up-quote");
		notificationSteps.push({
			type: "level_up_quote",
			newLevel,
			levelsGainedDelta: levelsGained,
			milestoneLevel: newLevel > 0 && newLevel % 10 === 0,
		});

		if (moves.length > 0) {
			slideIds.push("completion-level-up-moves");
			notificationSteps.push({ type: "level_up_moves" });
		}

		slideIds.push("completion-level-up-stat-select");
		// Second stat slide (rolled “stat increases”) only when the learner still has points
		// to assign on the first screen; at 0 budget the first slide is read-only CONTINUE-only.
		if (pools.statPointsForSlide > 0) {
			slideIds.push("completion-level-up-stat-received");
		}
		if (pools.statPointsForSlide > 0) {
			notificationSteps.push({
				type: "stat_points",
				points: pools.statPointsForSlide,
			});
		}

		slideIds.push("completion-level-up-ability");
		if (pools.abilityPointsForSlide > 0) {
			notificationSteps.push({
				type: "ability_points",
				points: pools.abilityPointsForSlide,
			});
		}
	}

	/** Last inserted = newest in inbox (matches prior: stop_completed after level-up rows). */
	notificationSteps.push({ type: "stop_completed" });

	return { slideIds, notificationSteps };
}
